import React, { Fragment } from 'react';
import { css } from '@emotion/react';
import AppStoreButtons from '@components/theme/AppStoreButtons';
import { Link } from 'gatsby';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UncontrolledAlert } from 'reactstrap';
import { faMapMarkerAlt, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { getBlockByName } from '@lib/blocks';
import {
  faFacebookF,
  faTwitter,
  faLinkedinIn
} from '@fortawesome/free-brands-svg-icons';
import Papercups from '@components/Papercups';
import styled from '@emotion/styled';
import { isInternalUrl } from '@lib/urls';

library.add(faFacebookF);
library.add(faTwitter);
library.add(faLinkedinIn);

const SocialIcons = styled.div`
  display: flex;
  text-decoration: none;
  color: white;
  margin-top: 2rem;
  .icon {
    margin-right: 1.5rem;
    border: 1px solid white;
    color: white;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: all 300ms;
    &.facebook:hover {
      background: #3b5998;
      border-color: #3b5998;
    }
    &.twitter:hover {
      background: #1da1f2;
      border-color: #1da1f2;
    }

    &.linkedin:hover {
      background: #0077b5;
      border-color: #0077b5;
    }
  }
`;

const titleStyle = css`
  font-size: 1.25rem;
  margin-top: 2rem;
  margin-bottom: 3rem;
  color: #fff;

  &::after {
    content: "";
    display: block;
    margin-top: 1rem;
    height: 4px;
    width: 50px;
    background: var(--primary);
  }
`;

export default function Footer({ language, layouts, configuration }) {
  const footer = getBlockByName(layouts.find(layout => getBlockByName(layout, 'LayoutPage_Block_Block_FooterSection')), 'LayoutPage_Block_Block_FooterSection');
  const { PAPERCUPS_ACCOUNT_ID, PAPERCUPS_BASE_URL } = Object.assign({}, ...configuration.constants.map(({ key, value }) => ({ [key]: value })));

  return (
    <div className="Page-Footer">
      <Papercups
        accountId={PAPERCUPS_ACCOUNT_ID}
        baseUrl={PAPERCUPS_BASE_URL}
        localization={{
          title: footer.papercupsTitle,
          subtitle: footer.papercupsSubtitles,
          greeting: footer.papercupsGreetings,
          placeholder: footer.papercupsPlaceholder
        }}
      />
      <div
        css={css`
          background: #1d1d1d;
          color: #ffffff;
          padding-bottom: 2rem;
        `}
      >
        <div className="container">
          <div className="row py-5">
            <div className="col-12 col-md-3">
              <h5 css={titleStyle}>{footer.sponsorsLabel} </h5>
              {footer.logos.map((item, i) => (
                <div className="mb-3" key={`img-footer-logo-${i}`}>
                  <img
                    src={item.logo.local.url}
                    alt={item.logo.altText}
                    css={css`
                      /* @md */
                      @media (min-width: 768px) {
                        max-width: 50%;
                      }
                    `}
                  />
                </div>
              ))}
            </div>
            <div className="col-12 col-md-6">
              <h5 css={titleStyle}>{footer.linksLabel}</h5>
              <div className="row">
                {footer.links.map(({ link }) => {
                  return (
                    <div key={link.url} className="col-12 col-md-6 mb-3">
                      {isInternalUrl(link.url) ? (
                        <Link
                          css={css`
                            color: #fff;
                            opacity: 0.5;
                            &::before {
                              content: ">";
                              padding-right: 0.4rem;
                            }
                            &:hover {
                              opacity: 1;
                              text-decoration: none;
                            }
                          `}
                          to={link.url}
                        >
                          {link.text}
                        </Link>
                      ) : (
                        <a
                          css={css`
                            color: #fff;
                            opacity: 0.5;
                            &::before {
                              content: ">";

                              padding-right: 0.4rem;
                            }
                            &:hover {
                              opacity: 1;
                              text-decoration: none;
                            }
                          `}
                          href={link.url}
                        >
                          {link.text}
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="col-12 col-md-3">
              <h5 css={titleStyle}>{footer.label}</h5>
              <div
                css={css`
                  opacity: 0.5;
                  margin-bottom: 1rem;
                `}
              >
                <FontAwesomeIcon
                  css={css`
                    color: var(--primary);
                    margin-right: 1rem;
                  `}
                  icon={faMapMarkerAlt}
                />
                {footer.address}
              </div>
              <div
                css={css`
                  opacity: 0.5;
                  margin-bottom: 1rem;
                `}
              >
                <FontAwesomeIcon
                  css={css`
                    color: var(--primary);
                    margin-right: 1rem;
                  `}
                  icon={faEnvelope}
                />{' '}
                <a
                  css={css`
                    color: #fff;
                  `}
                  href={`mailto:${footer.contactEmail}`}
                >
                  {footer.contactEmail}
                </a>
              </div>
              <SocialIcons>
                {footer.contactChannels.map((channel/*, i*/) => {
                  switch (channel.link.linkStyle) {
                    case 'facebook-f':
                      return (
                        <a key='channel-fb' href={channel.link.linkUrl} className="icon facebook">
                          {' '}
                          <FontAwesomeIcon icon={['fab', 'facebook-f']} />{' '}
                        </a>
                      );
                    case 'twitter':
                      return (
                        <a key='channel-tt' href={channel.link.linkUrl} className="icon twitter">
                          {' '}
                          <FontAwesomeIcon icon={['fab', 'twitter']} />{' '}
                        </a>
                      );
                    case 'linkedin-in':
                      return (
                        <a key='channel-li' href={channel.link.linkUrl} className="icon linkedin">
                          {' '}
                          <FontAwesomeIcon icon={['fab', 'linkedin-in']} />{' '}
                        </a>
                      );

                    default:
                      break;
                  }
                })}
              </SocialIcons>
            </div>
          </div>
        </div>
      </div>
      <div
        css={css`
          padding-top: 1rem;
          padding-bottom: 1rem;
          background: #191919;
          color: #444444;
        `}
      >
        <div className="container">
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              flex-direction: column;
              /* @sm */
              @media (min-width: 576px) {
                flex-direction: row;
              }
            `}
          >
            <span>{footer.subfooterMessage}</span>
            <AppStoreButtons links={footer.subfooterApps} />
            <span>{footer.subfooterCopyright}</span>
          </div>
        </div>
      </div>
    </div>
  );
}